import React, { useCallback, useEffect, useMemo, useState } from "react";
import Axios from "axios";
import { toast } from "react-toastify";
import { api_url } from "../../../config";
import Control from "../../common/Control";
import { EmptyState, ListPageHeader } from "../../common/DataList";
import { FicheSection, StickySaveBar } from "../../common/Fiche";
import {
  champParametre,
  estModifie,
  parametresAEnvoyer,
  saisiesInitiales,
  validerSaisie,
  validerSaisies,
} from "./parametres";
import "./parametres.scss";

const LIBELLE_ENREGISTRER = "Enregistrer";

// Une ligne de réglage : à gauche le libellé et son aide, à droite le champ
// (et son unité) ; les deux colonnes s'empilent sur un écran étroit.
const ChampParametre = ({ valeur, saisie, erreur, onChange, onBlur }) => {
  const champ = champParametre(valeur);
  const aideId = `${valeur.name}-aide`;
  const uniteId = `${valeur.name}-unite`;
  const decritPar = [
    champ.aide ? aideId : null,
    champ.unite ? uniteId : null,
    erreur ? `${valeur.name}-error` : null,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={`parametre parametre--${champ.type}`}>
      <div className="parametre__intitule">
        <label htmlFor={valeur.name} className="parametre__libelle">
          {champ.label}
        </label>
        {champ.aide && (
          <p id={aideId} className="parametre__aide">
            {champ.aide}
          </p>
        )}
      </div>
      <div className="parametre__saisie">
        <div className="parametre__controle">
          <Control
            margin="my-0"
            type={champ.type === "nombre" ? "number" : "text"}
            name={valeur.name}
            value={saisie}
            change={onChange}
            error={erreur ? { [valeur.name]: erreur } : {}}
            autocomplete="off"
            inputProps={{
              onBlur,
              "aria-describedby": decritPar || undefined,
              ...(champ.type === "nombre" ? { min: champ.min, step: 1, inputMode: "numeric" } : {}),
              ...(champ.type === "emails" ? { inputMode: "email", spellCheck: false } : {}),
            }}
          />
          {champ.unite && (
            <span id={uniteId} className="parametre__unite">
              {champ.unite}
            </span>
          )}
        </div>
        {champ.siVide && !String(saisie).trim() && (
          <p className="fiche-note parametre__note" role="status">
            <i className="fa fa-exclamation-triangle" aria-hidden="true" />
            {champ.siVide}
          </p>
        )}
      </div>
    </div>
  );
};

const ConstantDynamic = () => {
  const [groupes, setGroupes] = useState(null); // null : chargement en cours
  const [erreurChargement, setErreurChargement] = useState(false);
  const [reference, setReference] = useState({});
  const [saisies, setSaisies] = useState({});
  const [erreurs, setErreurs] = useState({});
  const [erreurEnregistrement, setErreurEnregistrement] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const repartirDe = (recus) => {
    const initiales = saisiesInitiales(recus);
    setGroupes(recus);
    setReference(initiales);
    setSaisies(initiales);
    setErreurs({});
  };

  const charger = useCallback(() => {
    setGroupes(null);
    setErreurChargement(false);
    Axios.get(api_url + "constants/dynamic")
      .then((res) => repartirDe(Array.isArray(res.data) ? res.data : []))
      .catch(() => setErreurChargement(true));
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  const valeurs = useMemo(
    () => Object.fromEntries((groupes || []).flatMap((g) => g.values.map((v) => [v.name, v]))),
    [groupes]
  );
  const modifie = useMemo(() => estModifie(saisies, reference), [saisies, reference]);

  // Vérifie un champ à la sortie (et, quand il est en erreur, à chaque saisie pour
  // retirer le message dès qu'il redevient juste). Une valeur inchangée n'est pas revérifiée.
  const verifier = (name, saisie) =>
    setErreurs((e) => ({
      ...e,
      [name]: saisie === reference[name] ? undefined : validerSaisie(champParametre(valeurs[name]), saisie) || undefined,
    }));

  const changer = (e) => {
    const { name, value } = e.target;
    setSaisies((s) => ({ ...s, [name]: value }));
    setErreurEnregistrement(null);
    if (erreurs[name]) verifier(name, value);
  };

  const enregistrer = () => {
    const trouvees = validerSaisies(groupes, saisies, reference);
    setErreurs(trouvees);
    const premiere = Object.keys(trouvees)[0];
    if (premiere) {
      document.getElementById(premiere)?.focus();
      return;
    }
    const corps = parametresAEnvoyer(groupes, saisies, reference);
    setIsSaving(true);
    setErreurEnregistrement(null);
    Axios.patch(api_url + "constants/dynamic", corps)
      .then(() => {
        repartirDe(corps);
        toast.success("Paramètres enregistrés");
      })
      .catch((err) =>
        setErreurEnregistrement(
          err?.response?.data?.other || "Les paramètres n'ont pas été enregistrés. Réessayez dans un instant."
        )
      )
      .finally(() => setIsSaving(false));
  };

  let contenu;
  if (erreurChargement) {
    contenu = (
      <section className="list-card" aria-label="Paramètres">
        <EmptyState
          erreur
          icon="fa-exclamation-triangle"
          title="Les paramètres n'ont pas pu être chargés."
          hint="Vérifiez votre connexion, puis réessayez."
          action={{ label: "Réessayer", onClick: charger }}
        />
      </section>
    );
  } else if (groupes === null) {
    contenu = (
      <section className="list-card parametres-page__chargement" aria-busy="true" aria-label="Chargement des paramètres">
        {[0, 1, 2].map((i) => (
          <div key={i} className="parametres-page__squelette">
            <span className="skeleton-bar parametres-page__squelette-titre" />
            <span className="skeleton-bar" />
          </div>
        ))}
      </section>
    );
  } else if (groupes.length === 0) {
    contenu = (
      <section className="list-card" aria-label="Paramètres">
        <EmptyState
          icon="fa-sliders-h"
          title="Aucun paramètre à régler"
          hint="Les paramètres apparaissent ici dès qu'ils sont installés sur la plateforme."
        />
      </section>
    );
  } else {
    contenu = (
      <>
        <div className="parametres-page__groupes">
          {groupes.map((groupe) => (
            <FicheSection key={groupe.name} id={`parametres-${groupe.name.toLowerCase()}`} title={groupe.label}>
              <div className="parametres-page__liste">
                {groupe.values.map((valeur) => (
                  <ChampParametre
                    key={valeur.name}
                    valeur={valeur}
                    saisie={saisies[valeur.name] ?? ""}
                    erreur={erreurs[valeur.name]}
                    onChange={changer}
                    onBlur={(e) => verifier(valeur.name, e.target.value)}
                  />
                ))}
              </div>
            </FicheSection>
          ))}
        </div>
        {erreurEnregistrement && (
          <p className="parametres-page__erreur" role="alert">
            <i className="fa fa-exclamation-circle" aria-hidden="true" />
            {erreurEnregistrement}
          </p>
        )}
        <StickySaveBar visible={modifie} isSaving={isSaving} onSave={enregistrer} label={LIBELLE_ENREGISTRER} />
      </>
    );
  }

  return (
    <div className="container-fluid px-5 my-5 list-page parametres-page">
      <ListPageHeader title="Paramètres" subtitle="Réglages de la plateforme, communs à tous les agents." />
      {contenu}
    </div>
  );
};

export default ConstantDynamic;
